import React, { useEffect, useState } from 'react';
import { check } from '@tauri-apps/plugin-updater';
import { relaunch } from '@tauri-apps/plugin-process';

const UpdateChecker = () => {
  const [updateInfo, setUpdateInfo] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, checking, available, downloading, error
  const [progress, setProgress] = useState(0);

  const isTauri = !!window.__TAURI_INTERNALS__;

  useEffect(() => {
    if (!isTauri) return;

    const checkForUpdates = async () => {
      try {
        setStatus('checking');
        const update = await check();
        if (update) {
          setUpdateInfo(update);
          setStatus('available');
        } else {
          setStatus('idle');
        }
      } catch (err) {
        console.error('Failed to check for updates:', err);
        setStatus('error');
      }
    };

    checkForUpdates();
    // Check every hour
    const interval = setInterval(checkForUpdates, 3600000);
    return () => clearInterval(interval);
  }, [isTauri]);

  const handleUpdate = async () => {
    if (!updateInfo) return;

    try {
      setStatus('downloading');
      let downloaded = 0;
      let total = 0;

      await updateInfo.downloadAndInstall((event) => {
        switch (event.event) {
          case 'Started':
            total = event.data.contentLength;
            console.log(`Update download started: ${total} bytes`);
            break;
          case 'Progress':
            downloaded += event.data.chunkLength;
            const pct = total > 0 ? Math.round((downloaded / total) * 100) : 0;
            setProgress(pct);
            break;
          case 'Finished':
            console.log('Update download finished');
            break;
          default:
            break;
        }
      });

      setStatus('relaunching');
      await relaunch();
    } catch (err) {
      console.error('Update failed:', err);
      setStatus('error');
    }
  };

  if (status === 'idle') return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9999]">
      {status === 'available' && (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl rounded-2xl p-4 w-80 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
               <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
               </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-zinc-900 dark:text-white">Update Available</h4>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 line-clamp-2"> Version {updateInfo.version} is ready to download.</p>
              <div className="mt-3 flex gap-2">
                <button 
                  onClick={handleUpdate}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                >
                  Update Now
                </button>
                <button 
                  onClick={() => setStatus('idle')}
                  className="px-3 py-1.5 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-600 dark:text-zinc-300 text-xs font-medium rounded-lg transition-colors"
                >
                  Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {status === 'downloading' && (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl rounded-2xl p-4 w-80 overflow-hidden">
          <h4 className="text-sm font-semibold text-zinc-900 dark:text-white">Downloading Update</h4>
          <div className="mt-3 relative h-1.5 w-full bg-zinc-100 dark:bg-zinc-800 rounded-full">
            <div 
              className="absolute left-0 top-0 h-full bg-blue-600 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-[10px] text-zinc-500 mt-2 text-right">{progress}%</p>
        </div>
      )}

      {status === 'relaunching' && (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl rounded-2xl p-4 w-80 text-center">
          <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2" />
          <p className="text-xs text-zinc-600 dark:text-zinc-400">Relaunching App...</p>
        </div>
      )}
    </div>
  );
};

export default UpdateChecker;
